/**
 * Top-level Sparrow-Landing shell.
 *
 * Same shape as the quill shell — thin top nav + content + footer —
 * because that pattern serves marketing/landing flows well across
 * both brands. Brand-distinct via the copper Sparrowmark + linen
 * background coming from index.css; layout chrome itself is shared
 * so the two apps feel like products from the same shop.
 *
 * The landing page (`/`) lets the hero own the full canvas; every
 * subpage gets the standard padded width.
 */

import { NavLink, Outlet, Link, useLocation } from "react-router-dom"
import { Sparrowmark } from "@/brand/Sparrowmark"
import { SPARROW_URL } from "@/lib/sparrow"
import {
  ArrowUpRight, Database, Home, ScanSearch, Sparkles,
} from "lucide-react"

const NAV = [
  { to: "/",            label: "Overview",     icon: Home },
  { to: "/samples",     label: "Samples",      icon: Database },
  { to: "/why-sparrow", label: "Why Sparrow",  icon: ScanSearch },
] as const

export function AppShell() {
  const loc = useLocation()
  const isLanding = loc.pathname === "/"
  return (
    <div className="min-h-screen flex flex-col text-foreground">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-background/70 border-b border-border">
        <div className="mx-auto max-w-7xl flex items-center justify-between gap-6 px-6 h-14">
          <Link to="/" className="text-primary hover:opacity-80 transition-opacity">
            <Sparrowmark size="md" />
          </Link>
          <nav className="flex items-center gap-1">
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  [
                    "px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors",
                    isActive
                      ? "bg-secondary text-secondary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60",
                  ].join(" ")
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a
              href={SPARROW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Try Sparrow
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </header>

      <main
        key={loc.pathname}
        className={
          (isLanding ? "flex-1" : "flex-1 mx-auto max-w-7xl w-full px-6 py-10") +
          " animate-page-fade"
        }
      >
        <Outlet />
      </main>

      <FooterMarker />
    </div>
  )
}

function FooterMarker() {
  return (
    <footer className="mt-auto border-t border-border">
      <div className="mx-auto max-w-7xl px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <Sparrowmark size="sm" iconOnly className="text-primary" />
          <span>Sparrow — statistics in your browser.</span>
        </div>
        <div className="flex flex-wrap gap-4">
          <a
            href={SPARROW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground transition-colors"
          >
            sparrow.dasc.nl
          </a>
          <Link to="/samples" className="hover:text-foreground transition-colors">
            Samples
          </Link>
          <Link to="/why-sparrow" className="hover:text-foreground transition-colors">
            Why Sparrow
          </Link>
          <span>v0.1 marketing site</span>
        </div>
      </div>
    </footer>
  )
}
